import { createContext, useContext, useState } from 'react';
import Cookies from 'universal-cookie';
import moment from 'moment';

import getViewType from 'View/host-schedule/helper/getViewType';

const HostScheduleContext = createContext();

const getSelectedInitialState = (viewType) => {
  const cookies = new Cookies();
  const timezone = cookies.get('organization')?.timezone;

  const params = new URLSearchParams(window.location.search);
  const todayWithTimezone = moment.tz(new Date(), timezone).format('YYYY-MM-DDTHH:mm:ssZ');

  const yearMonthQuery = params.get('yearMonth');
  const selectedYearMonth = moment(yearMonthQuery).format('MM-YYYY');
  const currentMonth = moment().format('MM-YYYY');

  const isCurrentMonth = currentMonth === selectedYearMonth;

  const startOfMonth = moment.tz(yearMonthQuery, timezone).startOf('month').format('YYYY-MM-DDTHH:mm:ssZ');
  const endOfMonth = moment.tz(yearMonthQuery, timezone).endOf('month').format('YYYY-MM-DDTHH:mm:ssZ');
  const currentStartAtDayWithTimezone = moment.tz(todayWithTimezone, timezone).startOf('day').format('YYYY-MM-DDTHH:mm:ssZ');
  const currentEndedAtDayWithTimezone = moment.tz(todayWithTimezone, timezone).endOf('day').format('YYYY-MM-DDTHH:mm:ssZ');

  const types = {
    upcoming: {
      startDate: isCurrentMonth ? currentStartAtDayWithTimezone : startOfMonth,
      endDate: endOfMonth,
      sort: 'endedAt.asc',
      search: '',
      page: 1,
    },
    past: {
      startDate: startOfMonth,
      endDate: isCurrentMonth ? currentEndedAtDayWithTimezone : endOfMonth,
      sort: 'endedAt.desc',
      search: '',
      page: 1,
    },
    scheduled: {
      startDate: startOfMonth,
      endDate: endOfMonth,
      sort: 'endedAt.asc',
      search: '',
      page: 1,
    },
  };

  const endDateWithTimezone = moment
    .tz(isCurrentMonth ? todayWithTimezone : yearMonthQuery, timezone)
    .add(2, 'day')
    .endOf('day')
    .format('YYYY-MM-DDTHH:mm:ssZ');

  const startDateWithTimezone = moment
    .tz(isCurrentMonth ? todayWithTimezone : yearMonthQuery, timezone)
    .startOf('day')
    .format('YYYY-MM-DDTHH:mm:ssZ');

  return {
    ...types[viewType],
    dateRangeCalendar: { startDate: startDateWithTimezone, endDate: endDateWithTimezone },
  };
};

const initialState = {
  previousDailySchedule: [],
  selectedView: 'card',
  selectedExpandedCard: null,
};

const initialFilterState = {
  calendarType: { value: 3, label: '3 Days' },
};

const HostScheduleProvider = ({ children }) => {
  const [state, setState] = useState(initialState);
  const [filterState, setFilterState] = useState(() => {
    const viewType = getViewType();
    const selectedInitialState = getSelectedInitialState(viewType);
    return { ...initialFilterState, ...selectedInitialState };
  });

  const updateState = (payload) => setState((prev) => ({ ...prev, ...payload }));
  const updateFilterState = (payload) => setFilterState((prev) => ({ ...prev, ...payload }));
  const resetState = () => setState(initialState);
  const resetFilterState = () => {
    const viewType = getViewType();
    const selectedInitialState = getSelectedInitialState(viewType);
    setFilterState({ ...initialFilterState, ...selectedInitialState });
  };

  return (
    <HostScheduleContext.Provider value={{ updateState, updateFilterState, resetState, resetFilterState, state, filterState }}>
      {children}
    </HostScheduleContext.Provider>
  );
};

export const useHostScheduleState = () => {
  return useContext(HostScheduleContext);
};

export default HostScheduleProvider;
